/**
 * Droite graduée RELATIVE partagée par les automatismes 3e 1234 (placer) et
 * 1235 (lire) : régimes de graduation, tirage des points, figure SVG avec
 * data-droite / data-points pour les solveurs, familles de leurres.
 *
 * Tout est calculé en DIXIÈMES ENTIERS (−27 = −2,7) : jamais de flottant
 * comparé. Le SVG reprend celui de gen_1064 / gen_1065 (5e) étendu à gauche
 * de 0 : la droite n'a pas d'origine à gauche, la flèche est à droite, les
 * graduations entières étiquetées portent le signe moins typographique (−).
 *
 * Régimes (cadrage programme-maths 2026-09-12, MM 6NA4) :
 *   entiers  : pas 1, fenêtre de 10 unités contenant 0 (−9..1 à −4..6),
 *              étiquettes 0 et multiples de 5 ; les points sont des entiers ;
 *   demis    : pas 0,5, fenêtre de 6 unités (−5..1 à −2..4), entiers
 *              étiquetés, points sur les demis seulement ;
 *   dixiemes : pas 0,1, fenêtre de 3 unités contenant 0 (−2..1, −1..2) ou
 *              autour d'un entier négatif (−8..−2), points sur les dixièmes
 *              non entiers.
 * SACRIFIÉ (fiches de 1234 et 1235) : −2,7, exclu de tout tirage, leurres compris.
 */
import { randomChoice, randInt, shuffle } from '../../../utils';

/** Le dixième sacrifié : −2,7. */
export const SACRIFIE_D = -27;

/** Dixièmes → « −2,7 » (signe moins typographique, pour l'affichage). */
export const frD = (d) => { const a = Math.abs(d); const s = `${Math.floor(a / 10)}${a % 10 ? `,${a % 10}` : ''}`; return d < 0 ? `−${s}` : s; };
/** Dixièmes → « -2,7 » (tiret du clavier, pour les réponses saisies). */
export const saisieD = (d) => frD(d).replace('−', '-');
/** Dixièmes → nombre décimal (pour numberline et data-*). */
export const dec = (d) => d / 10;

export const REGIMES = {
    entiers: () => { const s = -randInt(4, 9); return { nom: 'entiers', start: s * 10, end: (s + 10) * 10, pas: 10, div: 1 }; },
    demis: () => { const s = -randInt(2, 5); return { nom: 'demis', start: s * 10, end: (s + 6) * 10, pas: 5, div: 2 }; },
    dixiemes: () => {
        let s;
        if (randInt(0, 1)) s = -randInt(1, 2);
        else { const c = -randInt(2, 8); s = randomChoice([c - 2, c - 1]); }
        return { nom: 'dixiemes', start: s * 10, end: (s + 3) * 10, pas: 1, div: 10 };
    },
};
export const NOMS_REGIMES = Object.keys(REGIMES);
export const tirerRegime = (nom) => REGIMES[nom || randomChoice(NOMS_REGIMES)]();

/** Les entiers étiquetés (en dixièmes) : tous en demis/dixièmes ; 0 et les multiples de 5 en entiers. */
export const etiquettes = (reg) => {
    const r = [];
    for (let v = reg.start; v <= reg.end; v += 10) if (reg.nom !== 'entiers' || v % 50 === 0) r.push(v);
    return r;
};
/** Les repères passés au composant numberline : les étiquettes strictement intérieures. */
export const reperes = (reg) => etiquettes(reg).filter((v) => v > reg.start && v < reg.end).map(dec);

/** Les abscisses tirables : sur la grille, jamais aux bornes, jamais sur un entier étiqueté, jamais −2,7. */
export const grille = (reg, filtre = () => true) => {
    const et = etiquettes(reg);
    const r = [];
    for (let v = reg.start + reg.pas; v < reg.end; v += reg.pas) {
        if (v === SACRIFIE_D || et.includes(v)) continue;
        if (reg.nom !== 'entiers' && v % 10 === 0) continue;
        if (filtre(v)) r.push(v);
    }
    return r;
};
/** n abscisses distinctes, dont au moins `negatifs` négatives (les autres au hasard). */
export const tirerPoints = (reg, n, negatifs = 1) => {
    const neg = shuffle(grille(reg, (v) => v < 0)).slice(0, negatifs);
    const reste = shuffle(grille(reg, (v) => !neg.includes(v))).slice(0, n - neg.length);
    return shuffle([...neg, ...reste]);
};

const LETTRES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'K', 'L', 'M', 'N', 'P', 'R', 'S', 'T'];
export const tirerLettres = (n) => shuffle(LETTRES).slice(0, n);

/**
 * La droite graduée : points = [{ d, nom }] (d en dixièmes).
 * data-droite="start;end;div" et data-points="A:-2.7|B:1.5" (décimaux) pour les solveurs.
 */
export const svgDroite = (reg, points = []) => {
    const span = (reg.end - reg.start) / 10;
    const U = Math.min(90, Math.floor(280 / span)); const x0 = 24; const y = 44;
    const X = (d) => x0 + ((d - reg.start) / 10) * U;
    const et = etiquettes(reg);
    let s = `<line x1="${x0 - 12}" y1="${y}" x2="${X(reg.end) + 22}" y2="${y}" stroke="#1e293b" stroke-width="1.6"/>`
        + `<polygon points="${X(reg.end) + 22},${y} ${X(reg.end) + 14},${y - 4} ${X(reg.end) + 14},${y + 4}" fill="#1e293b"/>`;
    for (let v = reg.start; v <= reg.end; v += reg.pas) {
        const gros = v % 10 === 0;
        s += `<line x1="${X(v).toFixed(1)}" y1="${y - (gros ? 9 : 5)}" x2="${X(v).toFixed(1)}" y2="${y + (gros ? 9 : 5)}" stroke="#1e293b" stroke-width="${gros ? 1.6 : 1}"/>`;
        if (et.includes(v)) s += `<text x="${X(v).toFixed(1)}" y="${y + 24}" font-size="${span > 8 ? 11 : 12}" font-weight="700" fill="#1e293b" text-anchor="middle">${frD(v)}</text>`;
    }
    points.forEach((p) => {
        s += `<circle cx="${X(p.d).toFixed(1)}" cy="${y}" r="3.6" fill="#B45309" data-point="${p.nom}"/>`
            + `<text x="${X(p.d).toFixed(1)}" y="${y - 15}" font-size="13" font-weight="700" fill="#B45309" text-anchor="middle">${p.nom}</text>`;
    });
    const w = X(reg.end) + 30;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} 76" width="${w}" height="76" font-family="Arial" data-droite="${dec(reg.start)};${dec(reg.end)};${reg.div}" data-points="${points.map((p) => `${p.nom}:${dec(p.d)}`).join('|')}">${s}</svg>`;
};

export const INTRO = (reg) => (reg.div === 1 ? 'Sur cette droite graduée, les graduations vont de 1 en 1.' : `Sur cette droite graduée, chaque unité est partagée en ${reg.div}.`);

/**
 * Les leurres d'une abscisse x (dixièmes), dans la fenêtre et sur la grille :
 *   signe_inverse          : +2,7 pour −2,7 (le signe oublié ou ajouté) ;
 *   sens_inverse_negatif   : −1,3 pour −2,7 (de −2, on compte 7 vers 0, comme
 *                            pour un positif), PRINCIPALE ;
 *   rang_depuis_la_gauche  : −2,3 pour −2,7 (la 7e graduation après −3) ;
 *   decale_un_trait        : −2,6 / −2,8 ;
 *   decale_deux_traits     : ±2 pas (régime entiers seulement, en complément).
 */
export const leurresAbscisse = (x, reg) => {
    const c = [];
    const pousser = (code, v) => { if (v !== x && v !== SACRIFIE_D && v > reg.start && v < reg.end && v % reg.pas === 0 && !c.some((e) => e.v === v)) c.push({ code, v }); };
    pousser('signe_inverse', -x);
    if (x % 10 !== 0) {
        const plafond = Math.ceil(x / 10) * 10; const f = plafond - x;
        pousser('sens_inverse_negatif', plafond + f);
        pousser('rang_depuis_la_gauche', Math.floor(x / 10) * 10 + f);
    }
    pousser('decale_un_trait', x + reg.pas);
    pousser('decale_un_trait', x - reg.pas);
    if (c.length < 3) { pousser('decale_deux_traits', x + 2 * reg.pas); pousser('decale_deux_traits', x - 2 * reg.pas); }
    return c;
};
/** Trois leurres de codes distincts (le décalage compte pour un), ou null. */
export const troisLeurres = (cands) => {
    const pris = [];
    for (const e of shuffle(cands)) if (!pris.some((p) => p.code === e.code)) pris.push(e);
    if (pris.length < 3) for (const e of shuffle(cands)) if (pris.length < 3 && !pris.includes(e)) pris.push(e);
    return pris.length >= 3 ? pris.slice(0, 3) : null;
};
